/**
 * LaTeX compilation and image management API client
 */

import { apiClient } from './client'
import { log, logError, logDebug } from '@utils/logger'
import type {
  DocumentFile,
  DocumentFileCreate,
  DocumentFileUpdate,
  FileTreeResponse,
  ProjectCompileRequest,
  ProjectImportResponse
} from '../types'

// Types
export interface LatexCompileRequest {
  content: string
  compiler?: 'pdflatex' | 'xelatex' | 'lualatex'
  runs?: number
  include_bibtex?: boolean
}

export interface LatexCompileResponse {
  success: boolean
  warnings: string[]
  errors: string[]
  pdf_url?: string
  pdf_data?: string  // Base64-encoded PDF data
  message: string
  has_fatal_errors?: boolean  // True if compilation failed due to fatal errors
}

export interface ImageUploadResponse {
  success: boolean
  filename: string
  latex_path: string
  message: string
}

export interface SystemStatusResponse {
  latex_available: boolean
  tools: Record<string, boolean>
  message: string
}

export interface PackageInfoResponse {
  name: string
  installed: boolean
  info: string
}

export interface LatexTemplate {
  name: string
  description: string
  content: string
}

export interface LatexTemplatesResponse {
  templates: Record<string, LatexTemplate>
}

export interface LintError {
  line: number
  column: number
  message: string
  severity: 'error' | 'warning'
}

export interface LintResponse {
  errors: LintError[]
}

export interface SyncTeXRequest {
  page: number
  x: number
  y: number
  main_file_path?: string
}

export interface SyncTeXResponse {
  file_path: string | null
  line: number | null
  column: number | null
  found: boolean
}

export interface FileVersion {
  id: string
  file_id: string
  document_id: string
  version_number: number
  content: string
  file_size: number
  created_by: string | null
  created_at: string
  change_description: string | null
}

export interface FileVersionsListResponse {
  versions: FileVersion[]
  total: number
}

export interface RestoreFileVersionRequest {
  version_number: number
  create_new_version?: boolean
}

/**
 * LaTeX API client class
 */
export class LatexAPI {
  /**
   * Check LaTeX system status and available tools
   */
  async getSystemStatus(): Promise<SystemStatusResponse> {
    const response = await apiClient.get('/api/latex/status')
    return response.data
  }

  /**
   * Compile LaTeX document and get compilation info
   */
  async compileLatex(request: LatexCompileRequest): Promise<LatexCompileResponse> {
    const response = await apiClient.post('/api/latex/compile', request)
    return response.data
  }

  /**
   * Compile LaTeX document and get PDF directly
   */
  async compileLatexToPdf(request: LatexCompileRequest): Promise<Blob> {
    try {
      const response = await apiClient.getClient().post('/api/latex/compile/pdf', request, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      })
      
      return new Blob([response.data], { type: 'application/pdf' })
    } catch (error: any) {
      // Handle compilation errors
      if (error.response?.status === 422) {
        throw new LatexCompilationError(
          error.response.data?.message || 'LaTeX compilation failed',
          error.response.data?.errors || [],
          error.response.data?.warnings || []
        )
      }
      throw error
    }
  }

  /**
   * Compile LaTeX document to image for preview
   */
  async compileLatexToPreview(request: LatexCompileRequest): Promise<Blob> {
    const response = await apiClient.getClient().post('/api/latex/compile/preview', request, {
      responseType: 'blob',
      headers: {
        'Accept': 'image/png'
      }
    })
    
    return new Blob([response.data], { type: 'image/png' })
  }

  /**
   * Upload an image file for use in LaTeX documents
   */
  async uploadImage(file: File): Promise<ImageUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('file_type', file.type)
    logDebug('File type:', file.type)

    const response = await apiClient.getClient().post('/api/latex/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    return response.data
  }

  /**
   * Upload a base64-encoded image
   */
  async uploadImageBase64(imageData: string, filename: string): Promise<ImageUploadResponse> {
    const formData = new FormData()
    formData.append('image_data', imageData)
    formData.append('filename', filename)

    const response = await apiClient.post('/api/latex/images/upload-base64', formData)
    return response.data
  }

  /**
   * Get information about a LaTeX package
   */
  async getPackageInfo(packageName: string): Promise<PackageInfoResponse> {
    const response = await apiClient.get(`/api/latex/packages/${packageName}`)
    return response.data
  }

  /**
   * List available LaTeX packages
   */
  async listPackages(): Promise<string[]> {
    const response = await apiClient.get('/api/latex/packages')
    return response.data.packages
  }

  /**
   * Get LaTeX document templates
   */
  async getTemplates(): Promise<LatexTemplatesResponse> {
    const response = await apiClient.get('/api/latex/templates')
    return response.data
  }

  /**
   * Get LaTeX templates from disk
   */
  async getDiskTemplates(): Promise<{ templates: Array<{ name: string; description: string; file_count: number; has_main_tex: boolean }> }> {
    return apiClient.get('/api/latex/templates/disk')
  }

  /**
   * Helper method to create a downloadable PDF URL
   */
  createPdfUrl(pdfBlob: Blob, filename: string = 'document.pdf'): string {
    return URL.createObjectURL(pdfBlob)
  }

  /**
   * Helper method to download a PDF
   */
  downloadPdf(pdfBlob: Blob, filename: string = 'document.pdf'): void {
    const url = this.createPdfUrl(pdfBlob, filename)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Helper method to open PDF in new tab
   */
  openPdfInNewTab(pdfBlob: Blob): void {
    const url = this.createPdfUrl(pdfBlob)
    window.open(url, '_blank')
  }

  // ============================================================================
  // File Management Methods (LaTeX Project Structure)
  // ============================================================================

  /**
   * Get file tree structure for a LaTeX project
   */
  async getFileTree(documentId: string): Promise<FileTreeResponse> {
    // Use longer timeout for file tree (can be slow with many files)
    return apiClient.get<FileTreeResponse>(
      `/api/latex/documents/${documentId}/files/tree`,
      undefined,
      { timeout: 60000 } // 60 seconds for large file trees
    )
  }

  /**
   * Get file metadata by ID
   */
  async getFile(documentId: string, fileId: string): Promise<DocumentFile> {
    const response = await apiClient.get(`/api/latex/documents/${documentId}/files/${fileId}`)
    return response.data
  }

  /**
   * Get file metadata by path
   */
  async getFileByPath(documentId: string, path: string): Promise<DocumentFile> {
    const response = await apiClient.get(`/api/latex/documents/${documentId}/files`, {
      params: { path }
    })
    return response.data
  }

  /**
   * Get file content (download file)
   */
  async getFileContent(documentId: string, fileId: string): Promise<Blob> {
    const response = await apiClient.getClient().get(
      `/api/latex/documents/${documentId}/files/${fileId}/content`,
      { responseType: 'blob' }
    )
    return new Blob([response.data])
  }

  /**
   * Create a new file or folder
   */
  async createFile(documentId: string, fileData: DocumentFileCreate): Promise<DocumentFile> {
    const formData = new FormData()
    formData.append('name', fileData.name)
    formData.append('file_path', fileData.file_path)
    formData.append('file_type', fileData.file_type)
    if (fileData.parent_id) formData.append('parent_id', fileData.parent_id)
    if (fileData.content) formData.append('content', fileData.content)
    if (fileData.is_main_tex !== undefined) formData.append('is_main_tex', String(fileData.is_main_tex))

    const response = await apiClient.getClient().post(
      `/api/latex/documents/${documentId}/files`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  /**
   * Upload a file (binary or text)
   */
  async uploadFile(
    documentId: string,
    file: File,
    targetPath?: string,
    isMainTex: boolean = false
  ): Promise<DocumentFile> {
    const formData = new FormData()
    formData.append('file', file)
    if (targetPath) formData.append('target_path', targetPath)
    formData.append('is_main_tex', String(isMainTex))

    const response = await apiClient.getClient().post(
      `/api/latex/documents/${documentId}/files/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  /**
   * Upload multiple files in a single request
   */
  async uploadFilesBatch(
    documentId: string,
    files: File[],
    targetPath?: string
  ): Promise<{
    results: Array<{
      filename: string
      success: boolean
      file?: DocumentFile
      error?: string
      file_id?: string
      file_name?: string
      file_path?: string
    }>
    total: number
    successful: number
    failed: number
  }> {
    const formData = new FormData()
    // Append all files with the same field name 'files'
    files.forEach(file => {
      formData.append('files', file)
    })
    if (targetPath) formData.append('target_path', targetPath)

    const response = await apiClient.getClient().post(
      `/api/latex/documents/${documentId}/files/upload-batch`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  /**
   * Update a file (rename, update content, set as main .tex, or move to folder)
   */
  async updateFile(
    documentId: string,
    fileId: string,
    updateData: DocumentFileUpdate,
    forceVersion?: boolean
  ): Promise<DocumentFile> {
    const formData = new FormData()
    if (updateData.name) formData.append('name', updateData.name)
    if (updateData.content) formData.append('content', updateData.content)
    if (updateData.is_main_tex !== undefined) formData.append('is_main_tex', String(updateData.is_main_tex))
    // Handle parent_id: empty string means move to root (null), undefined means don't change
    if (updateData.parent_id !== undefined) {
      // If it's an empty string, we need to send it as null to the backend
      // But FastAPI Form(None) expects either a value or None, so we'll send empty string
      // and let the backend convert it
      formData.append('parent_id', updateData.parent_id === '' ? '' : updateData.parent_id)
    }
    // Force version creation for manual saves
    if (forceVersion) {
      formData.append('force_version', 'true')
    }

    const response = await apiClient.getClient().put(
      `/api/latex/documents/${documentId}/files/${fileId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  /**
   * Update file content (for binary files)
   */
  async updateFileContent(documentId: string, fileId: string, file: File): Promise<DocumentFile> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.getClient().put(
      `/api/latex/documents/${documentId}/files/${fileId}/content`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  /**
   * Delete a file or folder (recursively)
   */
  async deleteFile(documentId: string, fileId: string): Promise<void> {
    await apiClient.delete(`/api/latex/documents/${documentId}/files/${fileId}`)
  }

  /**
   * Export project as ZIP file
   */
  async exportProject(documentId: string): Promise<Blob> {
    const response = await apiClient.getClient().get(
      `/api/latex/documents/${documentId}/export`,
      { responseType: 'blob' }
    )
    return new Blob([response.data], { type: 'application/zip' })
  }

  /**
   * Import project from ZIP file
   */
  async importProject(
    documentId: string,
    zipFile: File,
    mainFileName?: string,
    onProgress?: (progress: number) => void
  ): Promise<ProjectImportResponse> {
    const formData = new FormData()
    formData.append('zip_file', zipFile)
    if (mainFileName) formData.append('main_file_name', mainFileName)

    // CLIENT-SIDE VALIDATION: Check file size before upload
    const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB
    if (zipFile.size > MAX_UPLOAD_SIZE) {
      const fileSizeMB = (zipFile.size / (1024 * 1024)).toFixed(2)
      const error = new Error(`File is too large (${fileSizeMB}MB). Maximum upload size is 50MB.`)
      ;(error as any).response = {
        status: 413,
        data: {
          detail: `File is too large (${fileSizeMB}MB). Maximum upload size is 50MB.`
        }
      }
      throw error
    }

    // Use longer timeout for file uploads
    // Content-Type will be automatically set by axios interceptor for FormData
    try {
      const response = await apiClient.getClient().post(
        `/api/latex/documents/${documentId}/import`,
        formData,
        { 
          timeout: 60000, // 60 seconds for large ZIP files
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total && progressEvent.total > 0) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              logDebug('[latexAPI.importProject] Upload progress:', {
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                progress
              })
              onProgress(progress)
            } else if (onProgress && progressEvent.loaded > 0) {
              // If total is not available, estimate based on loaded bytes
              // This is a fallback for cases where total might not be set immediately
              const estimatedProgress = Math.min(95, Math.round((progressEvent.loaded / (27 * 1024 * 1024)) * 100)) // Estimate based on typical file size
              logDebug('[latexAPI.importProject] Upload progress (estimated):', estimatedProgress)
              onProgress(estimatedProgress)
            }
          }
        }
      )
      return response.data
    } catch (error: any) {
      // Ensure 413 errors are properly propagated with error details
      if (error.response?.status === 413 || error.response?.statusCode === 413) {
        logError('[latexAPI.importProject] File too large (413):', {
          status: error.response?.status || error.response?.statusCode,
          data: error.response?.data,
          detail: error.response?.data?.detail
        })
        // Re-throw with proper error structure
        const enhancedError = new Error(error.response?.data?.detail || 'File is too large. Maximum upload size is 50MB.')
        ;(enhancedError as any).response = error.response || {
          status: 413,
          data: {
            detail: error.response?.data?.detail || 'File is too large. Maximum upload size is 50MB.'
          }
        }
        throw enhancedError
      }
      throw error
    }
  }

  /**
   * Get cached PDF if available
   */
  async getCachedPdf(
    documentId: string, 
    mainFilePath: string, 
    contentHash: string
  ): Promise<LatexCompileResponse | null> {
    try {
      const response = await apiClient.getClient().get<LatexCompileResponse>(
        `/api/latex/documents/${documentId}/cached-pdf`,
        {
          params: {
            main_file_path: mainFilePath,
            content_hash: contentHash
          },
          timeout: 10000  // 10 seconds for cache lookup
        }
      )
      return response.data
    } catch (error: any) {
      // 404 means not cached, which is fine
      if (error.response?.status === 404) {
        return null
      }
      // Other errors - log but don't fail
      logError('[getCachedPdf] Error checking cache:', error)
      return null
    }
  }

  /**
   * Compile LaTeX project using project file structure
   */
  async compileProject(documentId: string, request: ProjectCompileRequest): Promise<LatexCompileResponse> {
    try {
      // API now returns JSON with PDF data, errors, and warnings
      const response = await apiClient.getClient().post<LatexCompileResponse>(
        `/api/latex/documents/${documentId}/compile`,
        request,
        { 
          timeout: 240000, // 240 seconds (4 minutes) for LaTeX compilation (can take time with BibTeX and fallback engines)
          headers: { 'Accept': 'application/json' }
        }
      )
      
      return response.data
    } catch (error: any) {
      logError('[compileProject] Compilation error:', error)
      
      // Handle error responses
      if (error.response?.data) {
        const errorData = error.response.data
        
        // If it's already a LatexCompileResponse (from our new API), return it
        if (errorData.success !== undefined && Array.isArray(errorData.errors) && Array.isArray(errorData.warnings)) {
          return errorData as LatexCompileResponse
        }
        
        // Handle 500 errors or other server errors gracefully
        if (error.response?.status >= 500) {
          const errorMessage = errorData.detail || errorData.message || errorData || 'Server error during compilation. Please try again later.'
          throw new LatexCompilationError(
            errorMessage,
            Array.isArray(errorData.errors) ? errorData.errors : [errorMessage],
            Array.isArray(errorData.warnings) ? errorData.warnings : []
          )
        }
        
        // Legacy error format handling
        const detail = errorData.detail || errorData
        throw new LatexCompilationError(
          detail.message || detail || 'LaTeX compilation failed',
          Array.isArray(detail.errors) ? detail.errors : [],
          Array.isArray(detail.warnings) ? detail.warnings : []
        )
      }
      
      // Handle network errors or other unexpected errors
      if (error.code === 'ERR_NETWORK' || error.message) {
        throw new LatexCompilationError(
          error.message || 'Network error during compilation. Please check your connection and try again.',
          [],
          []
        )
      }
      
      // Fallback for any other error
      throw new LatexCompilationError(
        'LaTeX compilation failed. Please try again later.',
        [],
        []
      )
    }
  }

  /**
   * Add a BibTeX entry to a .bib file in the LaTeX project
   */
  async addBibtexEntry(
    documentId: string,
    bibtexEntry: string,
    bibFileId?: string
  ): Promise<DocumentFile> {
    const body: { bibtex_entry: string; bib_file_id?: string } = {
      bibtex_entry: bibtexEntry,
    }
    if (bibFileId) {
      body.bib_file_id = bibFileId
    }
    return apiClient.post(`/api/latex/documents/${documentId}/citations/add-bibtex`, body)
  }

  /**
   * Rescan LaTeX project to update metadata and citation index
   */
  async rescanProject(documentId: string): Promise<{
    main_file: string | null
    bibliography: any
    citations: any[]
    scanned_at: string
  }> {
    return apiClient.post(`/api/latex/documents/${documentId}/scan`)
  }

  /**
   * Search citations in the LaTeX project
   */
  async searchCitations(
    documentId: string,
    query?: string,
    limit: number = 50
  ): Promise<{ citations: any[]; count: number }> {
    const params = new URLSearchParams()
    if (query) {
      params.append('query', query)
    }
    params.append('limit', limit.toString())
    return apiClient.get(`/api/latex/documents/${documentId}/citations?${params.toString()}`)
  }

  /**
   * Get project metadata (.scidraft.json equivalent)
   */
  async getProjectMetadata(documentId: string): Promise<any> {
    return apiClient.get(`/api/latex/documents/${documentId}/metadata`)
  }

  /**
   * Lint LaTeX document
   */
  async lintLatex(content: string): Promise<LintResponse> {
    // apiClient.post already returns response.data, so we don't need .data
    const response = await apiClient.post<LintResponse>('/api/latex/lint', { content })
    return response
  }

  /**
   * Look up LaTeX source location from PDF coordinates using SyncTeX
   */
  async synctexLookup(
    documentId: string,
    request: SyncTeXRequest
  ): Promise<SyncTeXResponse> {
    return apiClient.post<SyncTeXResponse>(
      `/api/latex/documents/${documentId}/synctex`,
      request
    )
  }

  /**
   * Get version history for a file
   */
  async getFileVersions(
    documentId: string,
    fileId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<FileVersionsListResponse> {
    return apiClient.get<FileVersionsListResponse>(
      `/api/latex/documents/${documentId}/files/${fileId}/versions`,
      { limit, offset }
    )
  }

  /**
   * Get a specific version of a file
   */
  async getFileVersion(
    documentId: string,
    fileId: string,
    versionNumber: number
  ): Promise<FileVersion> {
    return apiClient.get<FileVersion>(
      `/api/latex/documents/${documentId}/files/${fileId}/versions/${versionNumber}`
    )
  }

  /**
   * Restore a file to a specific version
   */
  async restoreFileVersion(
    documentId: string,
    fileId: string,
    versionNumber: number,
    createNewVersion: boolean = true
  ): Promise<{ success: boolean; message: string; file: DocumentFile }> {
    return apiClient.post(
      `/api/latex/documents/${documentId}/files/${fileId}/versions/${versionNumber}/restore`,
      { version_number: versionNumber, create_new_version: createNewVersion }
    )
  }
}

/**
 * Custom error class for LaTeX compilation errors
 */
export class LatexCompilationError extends Error {
  constructor(
    message: string,
    public errors: string[],
    public warnings: string[]
  ) {
    super(message)
    this.name = 'LatexCompilationError'
  }
}

/**
 * Default LaTeX API instance
 */
export const latexAPI = new LatexAPI()

/**
 * Utility functions for LaTeX content
 */
export const LatexUtils = {
  /**
   * Create a complete LaTeX document from content
   */
  wrapInDocument(content: string, documentClass: string = 'article'): string {
    return `\\documentclass{${documentClass}}
\\usepackage[utf8]{inputenc}
\\usepackage{amsmath}
\\usepackage{amsfonts}
\\usepackage{amssymb}
\\usepackage{graphicx}
\\usepackage{hyperref}

\\begin{document}

${content}

\\end{document}`
  },

  /**
   * Insert image LaTeX code
   */
  insertImage(latexPath: string, caption?: string, width?: string, label?: string): string {
    let imageCode = `\\begin{figure}[h!]
  \\centering
  \\includegraphics`
    
    if (width) {
      imageCode += `[width=${width}]`
    }
    
    imageCode += `{${latexPath}}
`
    
    if (caption) {
      imageCode += `  \\caption{${caption}}
`
    }
    
    if (label) {
      imageCode += `  \\label{${label}}
`
    }
    
    imageCode += `\\end{figure}`
    
    return imageCode
  },

  /**
   * Common LaTeX packages for different use cases
   */
  getPackages: {
    math: ['amsmath', 'amsfonts', 'amssymb', 'mathtools'],
    graphics: ['graphicx', 'tikz', 'pgfplots'],
    tables: ['booktabs', 'longtable', 'array'],
    bibliography: ['biblatex', 'biber'],
    formatting: ['geometry', 'fancyhdr', 'setspace'],
    colors: ['xcolor', 'color'],
    code: ['listings', 'minted']
  },

  /**
   * Generate package import statements
   */
  generatePackageImports(packages: string[]): string {
    return packages.map(pkg => `\\usepackage{${pkg}}`).join('\n')
  },

  /**
   * Common LaTeX templates
   */
  templates: {
    minimal: '\\documentclass{article}\n\\begin{document}\nHello World!\n\\end{document}',
    
    article: `\\documentclass{article}
\\usepackage[utf8]{inputenc}
\\usepackage{amsmath}

\\title{Your Title}
\\author{Your Name}
\\date{\\today}

\\begin{document}
\\maketitle

\\section{Introduction}
Your content here.

\\end{document}`,

    letter: `\\documentclass{letter}
\\usepackage[utf8]{inputenc}

\\address{Your Address}
\\date{\\today}

\\begin{document}

\\begin{letter}{Recipient Address}

\\opening{Dear Sir/Madam,}

Your letter content here.

\\closing{Sincerely,}

\\end{letter}

\\end{document}`
  }
}